import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import "./machineTranslation.css";
import { ReactComponent as ArrowDown } from "../../../../assets/icons/HeaderIcons/arrow-gray-down.svg";
import InputPicker from "rsuite/InputPicker";
import "../inputPicker.css";
import { useTranslationLanguages } from "./useTranslationLanguages";
import { useLanguageDetection } from "./useLanguageDetection";
import { useTextTranslation } from "./useTextTranslation";

export type MachineTranslationNewProps = {
  /** Initial text to translate */
  initialText?: string;
  /** Optional source language code (if not provided, will auto-detect) */
  sourceLanguageCode?: string;
  /** Optional source language direction (ltr/rtl) */
  sourceLanguageDirection?: "ltr" | "rtl";
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed. Returns the translated text if translation was saved, null otherwise */
  onClose: (translatedText: string | null, details?: { fromLanguageCode: string; toLanguageCode: string }) => void;
};

/**
 * MachineTranslationNew Component
 *
 * Single Responsibility: Provides a UI for translating text from one language to another.
 * This component focuses solely on translation functionality and does not handle
 * manifest updates, audio store updates, or other business logic.
 *
 * Follows SOLID principles:
 * - Single Responsibility: Only handles translation UI and user interaction
 * - Open/Closed: Extensible via props without modification
 * - Dependency Inversion: Depends on abstractions (hooks) rather than concrete implementations
 */
const MachineTranslationNew: React.FC<MachineTranslationNewProps> = ({
  initialText = "",
  sourceLanguageCode,
  sourceLanguageDirection = "ltr",
  open,
  onClose,
}) => {
  // Language management
  const { languageOptions, isLoading: isLoadingLanguages, getLanguageByCode } = useTranslationLanguages();
  const { detectLanguage } = useLanguageDetection();
  const { translateText, isTranslating } = useTextTranslation();

  // State
  const [fromLanguageCode, setFromLanguageCode] = useState<string>(sourceLanguageCode || "");
  const [toLanguageCode, setToLanguageCode] = useState<string>("");
  const [translatedText, setTranslatedText] = useState<string>("");
  const [fromDirection, setFromDirection] = useState<"ltr" | "rtl">(sourceLanguageDirection);
  const [toDirection, setToDirection] = useState<"ltr" | "rtl">("ltr");
  const [inputText, setInputText] = useState<string>(initialText);
  const [hasText, setHasText] = useState<boolean>(initialText.trim().length > 0);

  const textToTranslateRef = useRef<HTMLTextAreaElement>(null);

  // Auto-detect language when modal opens and text is available, or when text changes
  useEffect(() => {
    if (open && inputText && inputText.trim().length > 0 && !sourceLanguageCode) {
      const performDetection = async () => {
        const detected = await detectLanguage(inputText);
        if (detected) {
          setFromLanguageCode(detected);
          const lang = getLanguageByCode(detected);
          if (lang) {
            setFromDirection(lang.direction as "ltr" | "rtl");
          }
        }
      };
      performDetection();
    }
  }, [open, inputText, sourceLanguageCode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setInputText(initialText);
      setTranslatedText("");
      setFromLanguageCode(sourceLanguageCode || "");
      setToLanguageCode("");
      setFromDirection(sourceLanguageDirection);
      setToDirection("ltr");
      setHasText(initialText.trim().length > 0);
    }
  }, [open, initialText, sourceLanguageCode, sourceLanguageDirection]);

  // Update input text when initialText prop changes
  useEffect(() => {
    if (initialText !== undefined) {
      setInputText(initialText);
      setHasText(initialText.trim().length > 0);
    }
  }, [initialText]);

  const handleFromLanguageChange = (value: string) => {
    setFromLanguageCode(value);
    const lang = getLanguageByCode(value);
    if (lang) {
      setFromDirection(lang.direction as "ltr" | "rtl");
    }
  };

  const handleToLanguageChange = (value: string) => {
    setToLanguageCode(value);
    const lang = getLanguageByCode(value);
    if (lang) {
      setToDirection(lang.direction as "ltr" | "rtl");
    }
  };

  const handleInputTextChange = (text: string) => {
    setInputText(text);
    setHasText(text.trim().length > 0);
  };

  const handleTranslate = async () => {
    const text = textToTranslateRef.current?.value || inputText;
    if (!text || !fromLanguageCode || !toLanguageCode) {
      return;
    }

    const result = await translateText(text, fromLanguageCode, toLanguageCode);
    if (result) {
      setTranslatedText(result);
    }
  };

  const handleSave = () => {
    onClose(translatedText || null, {
      fromLanguageCode,
      toLanguageCode,
    });
  };

  const handleCancel = () => {
    onClose(null);
  };

  const myProps = {
    caretComponent: ArrowDown,
  };

  const canTranslate = Boolean(toLanguageCode && fromLanguageCode && hasText && !isTranslating);
  const canSave = Boolean(translatedText);

  return (
    <Modal
      show={open}
      onHide={handleCancel}
      dialogClassName={"translation-modal"}
      backdrop={"static"}
      keyboard={false}
      bsPrefix={"mt"}
    >
      <Modal.Header closeButton>
        <Modal.Title className={"translation-title"}>
          <p>Translation</p>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        bsPrefix={"mt-body"}
        style={{
          backgroundColor: "#f5f5f5",
        }}
      >
        {/* From Language Selector */}
        <div className={"from-language-selector"}>
          <div className={"label-container"}>
            <label className={"text-area-label"} htmlFor="from-language-picker">
              From:
            </label>
          </div>
          <div className={"language-selection-container"}>
            <div className={"language-selector"}>
              {isLoadingLanguages ? (
                <div>Loading languages...</div>
              ) : (
                <InputPicker
                  {...myProps}
                  id="from-language-picker"
                  data={languageOptions}
                  value={fromLanguageCode}
                  appearance="default"
                  placeholder="Select a Language"
                  labelKey={"label"}
                  valueKey={"value"}
                  size={"sm"}
                  cleanable={false}
                  className={"language-dropdown"}
                  onChange={(e) => {
                    handleFromLanguageChange(e as string);
                  }}
                  menuMaxHeight={150}
                  disabled={!!sourceLanguageCode}
                />
              )}
            </div>
          </div>
        </div>

        {/* Input Text Area */}
        <textarea
          ref={textToTranslateRef}
          className={["col-sm-12", fromDirection, "from-text-area"].join(" ")}
          value={inputText}
          onChange={(e) => {
            handleInputTextChange(e.target.value);
          }}
          placeholder="Enter text to translate"
        />

        <hr className={"hr-style"} />

        {/* To Language Selector */}
        <div className={"to-language-selector"}>
          <div className={"label-container"}>
            <label className={"text-area-label"} htmlFor="to-language-picker">
              To:
            </label>
          </div>
          <div className={"language-selection-container"}>
            <div className={"language-selector"}>
              {isLoadingLanguages ? (
                <div>Loading languages...</div>
              ) : (
                <InputPicker
                  {...myProps}
                  id="to-language-picker"
                  data={languageOptions}
                  appearance="default"
                  placeholder="Select a Language"
                  labelKey={"label"}
                  valueKey={"value"}
                  size={"sm"}
                  cleanable={false}
                  className={"language-dropdown"}
                  onChange={(e) => {
                    handleToLanguageChange(e as string);
                  }}
                  menuMaxHeight={150}
                />
              )}
            </div>
          </div>
          <div className={"translate-button-container"}>
            <button className={"translate-button"} onClick={handleTranslate} disabled={!canTranslate}>
              {isTranslating ? "Translating..." : "Translate"}
            </button>
          </div>
        </div>

        {/* Output Text Area */}
        <textarea
          className={["col-sm-12", toDirection, "to-text-area"].join(" ")}
          value={translatedText}
          onChange={(e) => {
            setTranslatedText(e.target.value);
          }}
          placeholder="Translated text will appear here"
        />
      </Modal.Body>

      <Modal.Footer
        style={{
          backgroundColor: "#f5f5f5",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "60px",
        }}
      >
        <div className="translation-modal-buttons">
          <button onClick={handleCancel}>Cancel</button>
          <button onClick={handleSave} disabled={!canSave}>
            Save
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MachineTranslationNew;
